import { Column, HeaderText, Row, SmallText, Space, StandardText } from '@stenajs-webui/core';
import {
  Icon,
  Link,
  PrimaryButton,
  stenaClock,
  stenaLockClosed,
  stenaRefresh,
  stenaSignIn,
} from '@stenajs-webui/elements';
import type { FC } from 'react';
import { CONTACT_URL } from '../../common/footer/Footer';
import { Information } from '../../common/texts';
import type { AuthError } from '../../services/authentication/authReducer';
import { useAuth } from './AuthContext';

export interface AuthErrorBodyProps {
  error: AuthError;
}

export const AuthErrorBody: FC<AuthErrorBodyProps> = ({ error }) => {
  const auth = useAuth();

  if (
    error.type === 'invalid_token' &&
    error.description.includes('current time') &&
    error.description.includes('is before issued')
  ) {
    return (
      <Column alignItems={'flex-start'}>
        <Row alignItems={'center'}>
          <Icon icon={stenaClock} />
          <Space />
          <HeaderText>{Information.AuthTimingHeading}</HeaderText>
        </Row>
        <Space />
        <StandardText>{Information.AuthTimingExplanation}</StandardText>
        <Space num={2} />
        <Row spacing={0.5}>
          <SmallText variant={'bold'}>{Information.AuthTimingWindowsHelpHeader}</SmallText>
        </Row>
        <Row spacing={0.5}>
          <StandardText>{Information.AuthTimingStep1}</StandardText>
        </Row>
        <Row spacing={0.5}>
          <StandardText>{Information.AuthTimingStep2}</StandardText>
        </Row>
        <Row spacing={0.5}>
          <StandardText>{Information.AuthTimingStep3}</StandardText>
        </Row>
        <Row spacing={0.5}>
          <StandardText>{Information.AuthTimingStep4}</StandardText>
        </Row>
        <Row spacing={0.5}>
          <StandardText>{Information.AuthTimingStep5}</StandardText>
        </Row>
        <Row spacing={0.5}>
          <StandardText>{Information.AuthTimingStep6}</StandardText>
        </Row>
        <Row spacing={0.5}>
          <StandardText>{Information.AuthTimingStep7}</StandardText>
        </Row>
      </Column>
    );
  }

  if (error.type === 'timeout') {
    return (
      <Column alignItems={'flex-start'}>
        <Row alignItems={'center'}>
          <Icon icon={stenaClock} />
          <Space />
          <HeaderText>{Information.AuthTimeoutHeading}</HeaderText>
        </Row>
        <Space />
        <StandardText>{Information.AuthTimeoutBody}</StandardText>
        <Space num={2} />
        <Row>
          <PrimaryButton
            label={'Reload page'}
            leftIcon={stenaRefresh}
            onClick={() => window.location.reload()}
          />
        </Row>
      </Column>
    );
  }

  return (
    <Column alignItems={'flex-start'}>
      <Row alignItems={'center'}>
        <Icon icon={stenaLockClosed} />
        <Space />
        <HeaderText>{Information.AuthLoginFailedHeading}</HeaderText>
      </Row>
      <Space />
      <StandardText>{Information.AuthLoginFailedBody}</StandardText>
      <Space num={2} />
      <PrimaryButton label={'Go to login'} leftIcon={stenaSignIn} onClick={() => auth.logout()} />
      <Space num={2} />
      <StandardText>
        Please <Link href={CONTACT_URL}>contact your local booking office</Link> if you are still
        not able to log in to Freight Portal and need assistance with your bookings.
      </StandardText>
    </Column>
  );
};
